import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import API_BASE_URL from '../apiConfig';
import './NotesPnLLeaderboard.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const SORT_OPTIONS = [
  { value: 'totalPnl', label: 'Total P&L' },
  { value: 'avgPnl', label: 'Avg P&L / position' },
  { value: 'winRate', label: 'Win rate' },
  { value: 'positionCount', label: 'Position count' }
];

function NotesPnLLeaderboard({ tradeBookId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('totalPnl');

  useEffect(() => {
    if (!tradeBookId) {
      setData(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/trades/notes-leaderboard?tradeBookId=${tradeBookId}`
        );
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.error || 'Could not load notes leaderboard.');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tradeBookId]);

  const sortedRows = useMemo(() => {
    if (!data?.byTag?.length) return [];
    const rows = [...data.byTag];
    rows.sort((a, b) => b[sortBy] - a[sortBy]);
    return rows;
  }, [data, sortBy]);

  const chartProps = useMemo(() => {
    if (!sortedRows.length) return null;
    const top = sortedRows
      .slice()
      .sort((a, b) => Math.abs(b.totalPnl) - Math.abs(a.totalPnl))
      .slice(0, 8);
    const labels = top.map((r) => `${r.noteTypeLabel}: ${r.tagLabel}`.slice(0, 42));
    const values = top.map((r) => r.totalPnl);
    const bg = values.map((v) =>
      v >= 0 ? 'rgba(16, 185, 129, 0.75)' : 'rgba(239, 68, 68, 0.75)'
    );
    return {
      data: {
        labels,
        datasets: [
          {
            label: 'Total P&L (₹)',
            data: values,
            backgroundColor: bg,
            borderRadius: 8,
            borderSkipped: false
          }
        ]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          title: {
            display: true,
            text: 'Top tags by magnitude of total P&L',
            color: '#374151',
            font: { size: 14, weight: '600' }
          },
          tooltip: {
            callbacks: {
              label(ctx) {
                const v = ctx.parsed.x;
                return `Total P&L: ₹${Number(v).toFixed(2)}`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: { color: 'rgba(0,0,0,0.06)' },
            ticks: {
              callback(value) {
                return '₹' + Number(value).toFixed(0);
              }
            }
          },
          y: {
            grid: { display: false },
            ticks: { font: { size: 11 } }
          }
        }
      }
    };
  }, [sortedRows]);

  if (!tradeBookId) {
    return (
      <div className="notes-pnl-page">
        <div className="notes-pnl-card notes-pnl-card--empty">
          <p>Select a trade book to view notes and P&L.</p>
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="notes-pnl-page">
        <div className="notes-pnl-loading">
          <span className="notes-pnl-spinner" aria-hidden="true" />
          <span>Crunching tags and P&L…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notes-pnl-page">
        <div className="notes-pnl-alert" role="alert">
          {error}
        </div>
      </div>
    );
  }

  const baseline = data?.baseline || {};
  const coverage = data?.coverage || {};
  const nDone = coverage.completedPositionCount || 0;
  const tagged = coverage.withStructuredTagCount || 0;
  const tagPct = nDone > 0 ? Math.round((tagged / nDone) * 100) : 0;

  return (
    <div className="notes-pnl-page">
      <header className="notes-pnl-hero">
        <div className="notes-pnl-hero__copy">
          <h2 className="notes-pnl-hero__title">Journal Stats</h2>
          <p className="notes-pnl-hero__subtitle">
            Leaderboard of structured tags ranked by outcomes on completed positions. Same P&L rules
            as P&L Summary; tags from buy and sell legs are merged per symbol.
          </p>
        </div>
      </header>

      {nDone === 0 ? (
        <div className="notes-pnl-card notes-pnl-card--empty">
          <h3>No completed positions yet</h3>
          <p>
            Completed P&amp;L (and this view) appear once each symbol has both buy and sell quantity
            matched in this book.
          </p>
        </div>
      ) : (
        <>
          <div className="notes-pnl-metrics">
            <div className="notes-pnl-metric">
              <span className="notes-pnl-metric__label">Completed positions</span>
              <span className="notes-pnl-metric__value">{nDone}</span>
            </div>
            <div className="notes-pnl-metric">
              <span className="notes-pnl-metric__label">With structured tags</span>
              <span className="notes-pnl-metric__value">
                {tagged}
                <span className="notes-pnl-metric__suffix"> ({tagPct}%)</span>
              </span>
            </div>
            <div className={`notes-pnl-metric notes-pnl-metric--accent ${baseline.netPnl >= 0 ? 'is-profit' : 'is-loss'}`}>
              <span className="notes-pnl-metric__label">Book net P&amp;L</span>
              <span className="notes-pnl-metric__value">₹{Number(baseline.netPnl || 0).toFixed(2)}</span>
            </div>
            <div className="notes-pnl-metric">
              <span className="notes-pnl-metric__label">Book win rate</span>
              <span className="notes-pnl-metric__value">{Number(baseline.winRate || 0).toFixed(1)}%</span>
            </div>
          </div>

          {sortedRows.length === 0 ? (
            <div className="notes-pnl-card notes-pnl-card--empty">
              <h3>No structured tags on completed positions</h3>
              <p>
                Add context tags from <strong>Trade Wizard</strong> (Notes on saved rows). Reflections
                alone do not appear here — only dropdown tags drive this leaderboard.
              </p>
            </div>
          ) : (
            <>
              {chartProps && (
                <div className="notes-pnl-chart-card">
                  <div className="notes-pnl-chart-wrap">
                    <Bar data={chartProps.data} options={chartProps.options} />
                  </div>
                </div>
              )}

              <div className="notes-pnl-table-card">
                <div className="notes-pnl-table-head">
                  <h3 className="notes-pnl-table-title">Tag leaderboard</h3>
                  <label className="notes-pnl-sort">
                    <span className="notes-pnl-sort__label">Sort by</span>
                    <select
                      className="notes-pnl-sort__select"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      {SORT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <p className="notes-pnl-table-hint">
                  Each row is one tag value. A position can appear in multiple rows if it carries several
                  tags. Totals across rows are not additive to book P&amp;L.
                </p>
                <div className="notes-pnl-table-scroll">
                  <table className="notes-pnl-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Category</th>
                        <th>Tag</th>
                        <th>Positions</th>
                        <th>Total P&amp;L</th>
                        <th>Avg P&amp;L</th>
                        <th>Win rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((row, i) => (
                        <tr key={`${row.noteTypeKey}-${row.tagLabel}-${i}`}>
                          <td className="notes-pnl-rank">{i + 1}</td>
                          <td>{row.noteTypeLabel}</td>
                          <td>
                            <span className="notes-pnl-tag-pill">{row.tagLabel}</span>
                          </td>
                          <td>{row.positionCount}</td>
                          <td
                            className={
                              row.totalPnl > 0
                                ? 'is-profit'
                                : row.totalPnl < 0
                                  ? 'is-loss'
                                  : ''
                            }
                          >
                            ₹{row.totalPnl.toFixed(2)}
                          </td>
                          <td
                            className={
                              row.avgPnl > 0
                                ? 'is-profit'
                                : row.avgPnl < 0
                                  ? 'is-loss'
                                  : ''
                            }
                          >
                            ₹{row.avgPnl.toFixed(2)}
                          </td>
                          <td>{row.winRate.toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default NotesPnLLeaderboard;
